import { writable, get } from 'svelte/store';
import type { CalendarEvent } from '../types';
import { createSafePolygonHandler, type SafePolygonContext } from '../safePolygon';
import { isModalDialogOpen, popoversAreSheets } from '../sheetPopover';

export interface DayCellPopoverState {
  dateStr: string;
  dateLabel: string;
  events: CalendarEvent[];
  anchorEl: HTMLElement;
  /** Kept for HoveredDateLabel (which doesn't use Floating UI) */
  anchorRect: DOMRect;
}

export interface HoveredDateLabelState {
  dateStr: string;
  dateLabel?: string;
  anchorRect: DOMRect;
  anchorEl: HTMLElement;
}

export const dayCellPopoverState = writable<DayCellPopoverState | null>(null);
export const hoveredDateLabelState = writable<HoveredDateLabelState | null>(null);

// Which date is currently hovered (for week highlight)
export const hoveredDateStr = writable<string | null>(null);

let showTimeout: ReturnType<typeof setTimeout> | null = null;
let hideTimeout: ReturnType<typeof setTimeout> | null = null;

const SHOW_DELAY = 30;
const HIDE_DELAY = 200;

// --- Safe polygon state ---
let safePolygonCleanup: (() => void) | null = null;

/** Pending show request: stored when a new cell is entered while a safe polygon
 *  is active. If the polygon closes (cursor moved away), we fulfill the pending
 *  request so the new cell's popover appears. */
let pendingShowRequest: {
  dateStr: string;
  dateLabel: string;
  events: CalendarEvent[];
  anchorEl: HTMLElement;
} | null = null;

/** Continuously-tracked cursor position (used as the exit point when leaving a cell) */
let lastCursorX = 0;
let lastCursorY = 0;

function trackCursor(e: MouseEvent) {
  lastCursorX = e.clientX;
  lastCursorY = e.clientY;
}

/**
 * The floating element + its computed placement side, registered by
 * DayCellPopover.svelte once it has rendered and been positioned.
 * We store these so we can activate the safe polygon later — when
 * the cursor actually leaves the cell, not when the popover renders.
 */
let registeredFloatingEl: HTMLElement | null = null;
let registeredPlacementSide: 'top' | 'bottom' | 'left' | 'right' = 'bottom';

/**
 * Called by DayCellPopover.svelte after Floating UI positions the element.
 * Does NOT activate the safe polygon yet — just stores the info.
 */
export function registerFloatingElement(
  floatingEl: HTMLElement,
  placementSide: 'top' | 'bottom' | 'left' | 'right',
) {
  registeredFloatingEl = floatingEl;
  registeredPlacementSide = placementSide;
}

/**
 * Called by DayCellPopover.svelte when the popover unmounts / state clears.
 */
export function unregisterFloatingElement() {
  registeredFloatingEl = null;
}

/**
 * Actually activate the safe polygon. Called when the cursor leaves the
 * reference cell (hideDayCellPopover), using the cursor position AT THAT
 * MOMENT as the triangle origin point.
 */
function activateSafePolygon() {
  cleanupSafePolygon();

  const state = get(dayCellPopoverState);
  if (!state || !registeredFloatingEl) return;

  const ctx: SafePolygonContext = {
    x: lastCursorX,
    y: lastCursorY,
    placement: registeredPlacementSide,
    referenceEl: state.anchorEl,
    floatingEl: registeredFloatingEl,
    onClose: () => {
      hideDayCellPopover(true);
    },
  };

  const handler = createSafePolygonHandler(ctx, {
    buffer: 1,
    requireIntent: true,
  });

  document.addEventListener('mousemove', handler);
  safePolygonCleanup = () => {
    document.removeEventListener('mousemove', handler);
  };
}

function cleanupSafePolygon() {
  if (safePolygonCleanup) {
    safePolygonCleanup();
    safePolygonCleanup = null;
  }
}

// --- Public API (used by calendar views) ---

export function showDayCellPopover(
  dateStr: string,
  dateLabel: string,
  events: CalendarEvent[],
  anchorEl: HTMLElement,
) {
  // Always update hovered date immediately for week highlight
  hoveredDateStr.set(dateStr);

  const current = get(dayCellPopoverState);

  // If a safe polygon is actively protecting an existing popover and
  // the user is entering a DIFFERENT cell, don't show immediately.
  // Instead, store a pending request. If the polygon closes (cursor
  // moved away from the safe zone), the pending request is fulfilled
  // so the new cell's popover appears without needing another mouseenter.
  if (safePolygonCleanup && current && current.dateStr !== dateStr) {
    pendingShowRequest = { dateStr, dateLabel, events, anchorEl };
    return;
  }

  // Clear any pending request since we're proceeding with a show
  pendingShowRequest = null;

  // Cancel any pending hide
  if (hideTimeout) {
    clearTimeout(hideTimeout);
    hideTimeout = null;
  }

  // Cancel any pending show for a different cell
  if (showTimeout) {
    clearTimeout(showTimeout);
    showTimeout = null;
  }

  // Empty cells: show lightweight date label instead of full popover
  if (events.length === 0) {
    dayCellPopoverState.set(null);
    hoveredDateLabelState.set({
      dateStr,
      dateLabel,
      anchorRect: anchorEl.getBoundingClientRect(),
      anchorEl,
    });
    return;
  }

  // Has events: clear any date label, show popover
  hoveredDateLabelState.set(null);
  cleanupSafePolygon();

  showTimeout = setTimeout(() => {
    showTimeout = null;
    // Mobile: the tap that fired this hover also opened a dialog (edit event…).
    if (popoversAreSheets() && isModalDialogOpen()) return;
    dayCellPopoverState.set({
      dateStr,
      dateLabel,
      events,
      anchorEl,
      anchorRect: anchorEl.getBoundingClientRect(),
    });
  }, SHOW_DELAY);
}

export function hideDayCellPopover(immediate = false) {
  // Cancel any pending show
  if (showTimeout) {
    clearTimeout(showTimeout);
    showTimeout = null;
  }

  if (immediate) {
    if (hideTimeout) {
      clearTimeout(hideTimeout);
      hideTimeout = null;
    }
    cleanupSafePolygon();
    dayCellPopoverState.set(null);
    hoveredDateLabelState.set(null);
    hoveredDateStr.set(null);

    // If a new cell was entered while the safe polygon was active,
    // fulfill that pending request now that the old popover is closed.
    if (pendingShowRequest) {
      const req = pendingShowRequest;
      pendingShowRequest = null;
      showDayCellPopover(req.dateStr, req.dateLabel, req.events, req.anchorEl);
    }
    return;
  }

  // Mobile: the popover is a bottom sheet, closed only on purpose (backdrop,
  // swipe, Esc, an action), not by the mouseleave a phone fires on the next tap.
  if (popoversAreSheets() && get(dayCellPopoverState)) {
    return;
  }

  // If a safe polygon is already active, it owns the close decision.
  // This call is likely from an intermediate cell's mouseleave (the
  // corresponding mouseenter was blocked by showDayCellPopover).
  // Don't replace the polygon or start a new hide timeout.
  if (safePolygonCleanup) {
    return;
  }

  // If we have a rendered popover, activate safe polygon NOW —
  // the cursor is leaving the cell, so lastCursorX/Y is the exit point.
  if (registeredFloatingEl && get(dayCellPopoverState)) {
    activateSafePolygon();
  }

  // Fallback timeout in case the popover hasn't rendered yet or the
  // safe polygon never fires close (shouldn't happen, but safety net).
  hideTimeout = setTimeout(() => {
    if (!safePolygonCleanup) {
      dayCellPopoverState.set(null);
      hoveredDateLabelState.set(null);
      hoveredDateStr.set(null);
    }
    hideTimeout = null;
  }, HIDE_DELAY);
}

function cancelHideDayCellPopover() {
  if (hideTimeout) {
    clearTimeout(hideTimeout);
    hideTimeout = null;
  }
}

/**
 * Called when the cursor enters the floating popover element itself.
 * Cancels any pending hide and cleans up the safe polygon (no longer needed
 * since the user is now inside the popover).
 */
export function enterPopover() {
  cancelHideDayCellPopover();
  cleanupSafePolygon();
  pendingShowRequest = null;
}

/**
 * Called when the cursor leaves the floating popover element.
 * Starts a delayed hide (the user may re-enter the reference cell).
 */
export function leavePopover() {
  hideDayCellPopover();
}

// Install global cursor tracker
if (typeof window !== 'undefined') {
  document.addEventListener('mousemove', trackCursor, { passive: true });
}
